import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/common";
import { StatusBadge } from "@/components/common";
import { LinkDialog } from "@/components/LinkDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { UserPlus, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function SuperAdmins() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [link, setLink] = useState("");

  const { data, refetch } = useQuery({
    queryKey: ["super-admins"],
    queryFn: async () => (await api.get("/admin/super-admins")).data,
  });

  const invite = useMutation({
    mutationFn: async () => (await api.post("/admin/invite", { email, role: "super_admin" })).data,
    onSuccess: (d) => {
      toast.success(d.sent_status === "sent" ? "Invite emailed" : "Invite created (email not sent)");
      setOpen(false); setEmail(""); setLink(d.link); refetch();
    },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  return (
    <div>
      <PageHeader
        title="Super Admins"
        description="Infiblu staff with full access"
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="invite-super-admin-btn"><UserPlus className="mr-2 h-4 w-4" /> Invite super admin</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Invite super admin</DialogTitle></DialogHeader>
              <div><Label>Email</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5" data-testid="super-admin-email" /></div>
              <DialogFooter>
                <Button disabled={!email || invite.isPending} onClick={() => invite.mutate()} data-testid="super-admin-invite-submit">
                  {invite.isPending ? "Sending…" : "Send invite"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <Card className="divide-y divide-slate-100">
        {data?.active?.map((u) => (
          <div key={u.id} className="flex items-center justify-between px-5 py-3" data-testid={`super-admin-${u.email}`}>
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600"><ShieldCheck className="h-4 w-4" /></div>
              <div><p className="text-sm font-medium text-slate-800">{u.name}</p><p className="text-xs text-slate-400">{u.email}</p></div>
            </div>
            <StatusBadge status="active" />
          </div>
        ))}
      </Card>

      {data?.pending?.length > 0 && (
        <>
          <h2 className="mb-2 mt-8 text-sm font-semibold uppercase tracking-wide text-slate-400">Pending invites</h2>
          <Card className="divide-y divide-slate-100">
            {data.pending.map((p) => (
              <div key={p.id} className="flex items-center justify-between px-5 py-3" data-testid={`pending-super-${p.email}`}>
                <p className="text-sm text-slate-700">{p.email}</p>
                <StatusBadge status={p.sent_status} />
              </div>
            ))}
          </Card>
        </>
      )}

      <LinkDialog open={!!link} onClose={() => setLink("")} title="Invitation link"
        note="Share this link with the invitee." link={link} />
    </div>
  );
}
